import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  ActionIcon,
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';

import { enrollPasskey } from '@util/passkeyEnroll';
import { isWebAuthnAvailable } from '@util/passkeys';
import { suggestedPasskeyNickname } from '@util/suggestedPasskeyNickname';

export default function PasskeyManager() {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [naming, setNaming] = useState(false);
  const [nickname, setNickname] = useState(() => suggestedPasskeyNickname());
  const available = isWebAuthnAvailable();
  const nicknameReady = nickname.trim().length > 0;

  useEffect(() => {
    fetchPasskeys();
  }, []);

  function fetchPasskeys() {
    axios
      .post('/retrieve-passkeys/', {})
      .then(({ data }) => {
        setPasskeys(data.response || []);
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  function onStartAdd() {
    if (!available) {
      toast.error('Passkeys are not supported in this browser.');
      return;
    }
    setNickname(suggestedPasskeyNickname());
    setNaming(true);
  }

  function onCancelNaming() {
    setNaming(false);
  }

  async function onCreatePasskey() {
    if (!available) {
      toast.error('Passkeys are not supported in this browser.');
      return;
    }

    if (!nicknameReady) {
      toast.error(
        'Give this passkey a nickname that describes the device, like My iPhone.',
      );
      return;
    }

    setLoading(true);
    try {
      await enrollPasskey(nickname);
      setNickname(suggestedPasskeyNickname());
      setNaming(false);
      toast.success('Passkey added.');
      fetchPasskeys();
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        toast.error('Passkey prompt was cancelled.');
      } else {
        toast.error(typeof err === 'string' ? err : err?.message || err);
      }
    } finally {
      setLoading(false);
    }
  }

  function onDelete(passkeyId) {
    axios
      .delete(`/passkeys/${passkeyId}/`)
      .then(() => {
        toast.success('Passkey removed.');
        fetchPasskeys();
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  return (
    <Card mt="lg" withBorder>
      <Stack gap="sm">
        <Text fw={600}>Passkeys</Text>
        <Text size="sm" c="dimmed">
          Use Face ID, Touch ID, or your device lock to sign in without a
          password.
        </Text>
        {!available && (
          <Text size="sm" c="red">
            This browser does not support passkeys. Try Chrome, Safari, or Edge
            over HTTPS.
          </Text>
        )}
        {passkeys.map((passkey) => (
          <Group key={passkey.passkey_id} justify="space-between">
            <div>
              <Text size="sm">{passkey.nickname}</Text>
              <Text size="xs" c="dimmed">
                Added{' '}
                {passkey.created_at
                  ? new Date(passkey.created_at).toLocaleString()
                  : ''}
              </Text>
            </div>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => onDelete(passkey.passkey_id)}
              aria-label="Remove passkey"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
        {naming ? (
          <>
            <TextInput
              label="Device nickname"
              description="Suggested from this browser — edit if you want something clearer, e.g. Work MacBook."
              placeholder={suggestedPasskeyNickname() || 'My iPhone'}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              disabled={loading}
              required
            />
            <Group>
              <Button
                variant="subtle"
                color="gray"
                onClick={onCancelNaming}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={onCreatePasskey}
                loading={loading}
                disabled={!nicknameReady}
              >
                Create passkey
              </Button>
            </Group>
          </>
        ) : (
          <Button onClick={onStartAdd} disabled={!available}>
            Add passkey
          </Button>
        )}
      </Stack>
    </Card>
  );
}
